import { supabase } from '../lib/supabase';

export const emailTemplateService = {
  // Get all email templates
  async getAllTemplates() {
    const { data, error } = await supabase
      ?.from('email_templates')
      ?.select('*')
      ?.order('form_type', { ascending: true });
    
    if (error) throw error;
    return data;
  },

  // Get template by form type
  async getTemplateByFormType(formType) {
    const { data, error } = await supabase
      ?.from('email_templates')
      ?.select('*')
      ?.eq('form_type', formType)
      ?.eq('is_active', true)
      ?.single();
    
    if (error) throw error;
    return data;
  },

  // Update email template
  async updateTemplate(templateId, updates) {
    const { data, error } = await supabase
      ?.from('email_templates')
      ?.update(updates)
      ?.eq('id', templateId)
      ?.select()
      ?.single();
    
    if (error) throw error;
    return data;
  },

  // Render template with dynamic field mapping
  renderTemplate(template, formData) {
    if (!template || !formData) return '';

    let renderedHtml = template?.html_template || '';
    let renderedSubject = template?.subject_template || '';

    // Replace all field placeholders with actual data
    const replaceFields = (text) => {
      return text?.replace(/\{\{([^}]+)\}\}/g, (match, fieldPath) => {
        const trimmedPath = fieldPath?.trim();
        
        // Handle nested fields (e.g., metadata.country)
        if (trimmedPath?.includes('.')) {
          const parts = trimmedPath?.split('.');
          let value = formData;
          
          for (const part of parts) {
            value = value?.[part];
            if (value === undefined || value === null) {
              return '';
            }
          }
          
          return value || '';
        }
        
        // Handle direct fields
        return formData?.[trimmedPath] || '';
      });
    };

    renderedHtml = replaceFields(renderedHtml);
    renderedSubject = replaceFields(renderedSubject);

    return {
      html: renderedHtml,
      subject: renderedSubject
    };
  },

  // Send test email with preview
  async sendTestEmail(templateId, testData, recipientEmail) {
    try {
      // Get template
      const { data: template, error: templateError } = await supabase
        ?.from('email_templates')
        ?.select('*')
        ?.eq('id', templateId)
        ?.single();
      
      if (templateError) throw templateError;

      // Render template with test data
      const rendered = this.renderTemplate(template, testData);

      // Call Edge Function to send test email
      const { data, error } = await supabase?.functions?.invoke('send-form-notification', {
        body: {
          formType: template?.form_type,
          name: testData?.name || 'Test User',
          email: recipientEmail,
          phone: testData?.phone || '',
          subject: rendered?.subject,
          message: testData?.message || 'This is a test email',
          metadata: testData?.metadata || {},
          isTestEmail: true,
          customTemplate: {
            html: rendered?.html,
            subject: rendered?.subject
          }
        }
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Failed to send test email:', error);
      throw error;
    }
  },

  // Get sample data for form type
  getSampleData(formType) {
    const sampleData = {
      contact: {
        name: 'John Doe',
        email: 'john.doe@example.com',
        phone: '+1 (555) 123-4567',
        subject: 'Website Development Inquiry',
        message: 'I am interested in developing a new website for my business. Could we schedule a consultation to discuss the project requirements and timeline?',
        metadata: {
          country: 'United States',
          projectType: 'Web Development',
          budgetRange: '$5,000 - $10,000',
          preferredContact: 'Email'
        }
      },
      consultation: {
        name: 'Jane Smith',
        email: 'jane.smith@example.com',
        phone: '+1 (555) 987-6543',
        subject: 'Consultation Booking',
        message: 'Looking forward to discussing my project requirements with you.',
        metadata: {
          service: 'Web Development Consultation',
          timeSlot: 'Monday, January 15, 2026 at 2:00 PM EST'
        }
      },
      partnership: {
        name: 'Michael Johnson',
        email: 'michael@techcorp.com',
        phone: '+1 (555) 456-7890',
        subject: 'Partnership Inquiry',
        message: 'We are interested in exploring a strategic partnership opportunity. Our company specializes in cloud infrastructure and we believe there could be great synergy with your services.',
        metadata: {
          companyName: 'TechCorp Solutions',
          partnershipType: 'Strategic Alliance',
          industry: 'Technology',
          website: 'https://www.techcorp.com'
        }
      }
    };

    return sampleData?.[formType] || {};
  }
};